/**
 * Parsers + graph model builder for the aGM "Cluster Formation" page.
 *
 * The combined curation query returns one row shaped like:
 *   entity_type, entity_domain, entity_id,
 *   flatten_neighbors_array : ARRAY<STRING>          "type~domain~id" values
 *   cluster_list            : ARRAY<STRING>          "type~domain~id" values
 *   final_graph_map         : ARRAY<STRUCT<key, value ARRAY<STRING>>>
 *
 * Pail hands these back as Athena/BigQuery *text renderings* rather than JSON,
 * e.g. `[a b c]` for arrays and `[[key [v1 v2]] [key [v3]]]` for the map, so the
 * parsers below accept the text form, real JSON, and already-decoded JS values.
 */

/** Nothing below this many IDs in a neighbor group is worth collapsing. */
export const NEIGHBOR_GROUP_MIN = 1
/** Individual neighbor dots drawn per cluster before the rest collapse into a bundle. */
export const NEIGHBOR_EXPAND_CAP = 12
export const NEIGHBOR_EXPAND_STEP = 60
export const NEIGHBOR_EXPAND_CAP_MAX = 60
/** IDs listed in the detail panel for a collapsed bundle before "show all". */
export const BUNDLE_ID_PREVIEW = 250

/**
 * Split a bracketed text array into its top-level elements.
 * Whitespace separates elements; nested `[...]` groups stay intact.
 *   "[a b [c d]]" -> ["a", "b", "[c d]"]
 */
export function splitTopLevel(text) {
    const s = String(text ?? '').trim()
    if (!s) return []
    const body = s.startsWith('[') && s.endsWith(']') ? s.slice(1, -1) : s
    const out = []
    let depth = 0
    let buf = ''
    for (let i = 0; i < body.length; i++) {
        const ch = body[i]
        if (ch === '[' || ch === '{') depth++
        else if (ch === ']' || ch === '}') depth--
        if (depth === 0 && /\s|,/.test(ch)) {
            if (buf) out.push(buf)
            buf = ''
            continue
        }
        buf += ch
    }
    if (buf) out.push(buf)
    return out.map((x) => x.trim()).filter(Boolean)
}

/**
 * Flat list of "type~domain~id" strings from any of the shapes Pail may return
 * (JS array, JSON string, or the `[a b c]` text rendering).
 */
export function parseTildeList(raw) {
    if (raw == null || raw === '') return []
    if (Array.isArray(raw)) return raw.flatMap((x) => parseTildeList(x))
    if (typeof raw === 'object') return Object.values(raw).flatMap((x) => parseTildeList(x))

    const s = String(raw).trim()
    if (!s || s === '[]' || s === 'null') return []
    if (s.startsWith('[') || s.startsWith('{')) {
        try {
            const j = JSON.parse(s)
            if (j !== null && typeof j === 'object') return parseTildeList(j)
        } catch (_) {
            /* not JSON — fall through to the text rendering */
        }
        return splitTopLevel(s).flatMap((x) => parseTildeList(x))
    }
    return s
        .split(/[\s,|]+/)
        .map((x) => x.trim())
        .filter(Boolean)
}

/** "ck~nypost~abc" -> { type, domain, id, key }. Extra `~` stay in the id. */
export function parseEntityKey(raw) {
    const key = String(raw ?? '').trim()
    if (!key) return null
    const parts = key.split('~')
    if (parts.length < 3) {
        return { key, type: parts[0] || 'unknown', domain: parts[1] || '', id: parts[parts.length - 1] || key }
    }
    return {
        key,
        type: parts[0],
        domain: parts[1],
        id: parts.slice(2).join('~'),
    }
}

export function buildEntityKey(type, domain, id) {
    return `${String(type ?? '').trim()}~${String(domain ?? '').trim()}~${String(id ?? '').trim()}`
}

/** "ck~nypost~abc" -> "ck~nypost" (the color/legend dimension). */
export function typeDomainOf(key) {
    const p = parseEntityKey(key)
    return p ? `${p.type}~${p.domain}` : 'unknown'
}

/**
 * Normalize a `final_graph_map` struct key.
 *
 * The query CONCATs it to "type~domain~id", but the CSV export of an earlier
 * struct shape renders the three fields space-separated as `id type domain`.
 * Prefer whichever ordering is a known cluster; otherwise assume the CSV order.
 */
function normalizeMapKey(parts, knownKeys) {
    const clean = parts.map((p) => String(p).trim()).filter(Boolean)
    if (!clean.length) return null
    if (clean.length === 1) return clean[0]
    const candidates =
        clean.length === 3
            ? [`${clean[1]}~${clean[2]}~${clean[0]}`, clean.join('~')]
            : [clean.join('~')]
    if (knownKeys && knownKeys.size) {
        const hit = candidates.find((c) => knownKeys.has(c))
        if (hit) return hit
    }
    return candidates[0]
}

/**
 * `final_graph_map` -> [{ key, values: [...] }], duplicate keys merged.
 * The query CROSS JOINs clusters against the curation table, so the same
 * cluster key legitimately appears in several structs with different values.
 */
export function parseFinalGraphMap(raw, knownKeys) {
    if (raw == null || raw === '') return []

    const merged = new Map()
    const add = (key, values) => {
        if (!key) return
        const prev = merged.get(key) || []
        merged.set(key, prev.concat(values))
    }

    const consumeEntry = (entry) => {
        if (entry == null) return
        // { key, value } / { key, values } — real JSON from Pail
        if (!Array.isArray(entry) && typeof entry === 'object') {
            const k = entry.key ?? entry.k
            const v = entry.value ?? entry.values ?? entry.v
            if (k != null) add(normalizeMapKey([k], knownKeys), parseTildeList(v))
            return
        }
        // ["key", ["v1", "v2"]] — tuple JSON
        if (Array.isArray(entry)) {
            const values = parseTildeList(entry[entry.length - 1])
            add(normalizeMapKey(entry.slice(0, -1), knownKeys), values)
            return
        }
        // "{key=k, value=[v1, v2]}" — Athena's text rendering of a ROW
        const row = /^\{\s*key=(.*?),\s*value=(\[.*\]|null)\s*\}$/s.exec(String(entry).trim())
        if (row) {
            add(normalizeMapKey([row[1]], knownKeys), parseTildeList(row[2]))
            return
        }
        // "[key [v1 v2]]" / "[id type domain [v1 v2]]" — text rendering
        const tokens = splitTopLevel(String(entry))
        if (!tokens.length) return
        const last = tokens[tokens.length - 1]
        const hasValueArray = last.startsWith('[')
        const values = hasValueArray ? parseTildeList(last) : []
        const keyTokens = hasValueArray ? tokens.slice(0, -1) : tokens
        add(normalizeMapKey(keyTokens, knownKeys), values)
    }

    if (Array.isArray(raw)) raw.forEach(consumeEntry)
    else if (typeof raw === 'object') Object.values(raw).forEach(consumeEntry)
    else {
        const s = String(raw).trim()
        if (!s || s === '[]' || s === 'null') return []
        let handled = false
        if (s.startsWith('[') || s.startsWith('{')) {
            try {
                const j = JSON.parse(s)
                if (Array.isArray(j)) {
                    j.forEach(consumeEntry)
                    handled = true
                } else if (j && typeof j === 'object') {
                    Object.entries(j).forEach(([k, v]) => add(normalizeMapKey([k], knownKeys), parseTildeList(v)))
                    handled = true
                }
            } catch (_) {
                /* text rendering */
            }
        }
        if (!handled) splitTopLevel(s).forEach(consumeEntry)
    }

    return [...merged.entries()].map(([key, values]) => ({
        key,
        values: [...new Set(values.map(String).filter(Boolean))],
    }))
}

/**
 * Turn one query row into the three-level graph the page draws.
 *
 *   level 0  root      the entity the user asked about
 *   level 1  cluster   every key in final_graph_map / cluster_list
 *   level 2  neighbor  each cluster's value array, grouped by type~domain
 *
 * A neighbor that also appears in `flatten_neighbors_array` is *shared*: it is a
 * neighbor of the root AND a neighbor of the cluster, so it is the actual point
 * of contact between the two. It stays a member of its cluster and gains a
 * second, dashed edge straight back to the root — that edge is the connection.
 * Entries of `flatten_neighbors_array` that no cluster holds are not drawn.
 *
 * @param {object}  row       parsed query row
 * @param {object}  opts
 * @param {string} opts.mode      'connects' | 'all' | 'grouped'
 * @param {number} opts.expandCap  individual neighbor nodes drawn per cluster
 * @param {string} opts.filter     case-insensitive substring filter on neighbor keys
 */
export function buildClusterFormationGraph(row, opts = {}) {
    /* 'connects' (default) | 'all' | 'grouped' — see the per-cluster emission below. */
    const mode = opts.mode || 'connects'
    const expandCap = Math.min(
        NEIGHBOR_EXPAND_CAP_MAX,
        Math.max(1, Number(opts.expandCap) || NEIGHBOR_EXPAND_CAP)
    )
    const filter = String(opts.filter || '').trim().toLowerCase()

    const rootKey = buildEntityKey(row.entity_type, row.entity_domain, row.entity_id)
    const flatten = [...new Set(parseTildeList(row.flatten_neighbors_array))]
    const flattenSet = new Set(flatten)
    const clusterList = [...new Set(parseTildeList(row.cluster_list))]
    const graphMap = parseFinalGraphMap(row.final_graph_map, new Set(clusterList))

    /* Every cluster we know about — from the map first (it carries the members),
       then any cluster_list entry the map had nothing for. */
    const byKey = new Map()
    graphMap.forEach((e) => byKey.set(e.key, { key: e.key, values: e.values }))
    clusterList.forEach((k) => {
        if (!byKey.has(k)) byKey.set(k, { key: k, values: [] })
    })
    const clusters = [...byKey.values()]

    /* Moves the user has staged redraw their ids under the destination cluster,
       so the picture shows the formation as it would be after saving. */
    const movedIn = applyClusterMovesToClusters(clusters, opts.moves)
    const movedIdsFor = (clusterKey, ids) => {
        const set = movedIn.get(clusterKey)
        return set ? ids.filter((v) => set.has(v)) : []
    }
    const movedCountFor = (clusterKey, ids) => movedIdsFor(clusterKey, ids).length

    const matches = (k) => !filter || k.toLowerCase().includes(filter)

    const nodes = []
    const edges = []
    const edgeKeys = new Set()
    const pushEdge = (source, target, linkKind) => {
        const ek = `${source}->${target}`
        if (edgeKeys.has(ek)) return
        edgeKeys.add(ek)
        edges.push({ source, target, linkKind })
    }

    const rootId = 'root'
    const rootParsed = parseEntityKey(rootKey)
    nodes.push({
        id: rootId,
        type: 'root',
        level: 0,
        key: rootKey,
        entityType: rootParsed.type,
        entityDomain: rootParsed.domain,
        entityId: rootParsed.id,
        neighborType: `${rootParsed.type}~${rootParsed.domain}`,
        flattenCount: flatten.length,
        clusterCount: clusters.length,
    })

    /* Neighbors covered by at least one cluster — the leftovers hang off the root. */
    const coveredByCluster = new Set()
    let sharedTotal = 0
    let neighborTotal = 0

    const clusterSummaries = []

    clusters.forEach((cluster, ci) => {
        const parsed = parseEntityKey(cluster.key)
        const all = cluster.values
        const shared = all.filter((v) => flattenSet.has(v))
        all.forEach((v) => coveredByCluster.add(v))

        /* A cluster routinely lists the queried entity among its own members —
           that is what makes it your cluster. Drawing it again as a member is
           the same node twice, and when it is the only leftover it surfaced as
           a mystery "+1 not shown". Flag it on the cluster instead. */
        const includesSelf = all.includes(rootKey)
        const visible = all.filter((v) => matches(v) && v !== rootKey)
        neighborTotal += all.length
        sharedTotal += shared.length

        const clusterId = `cluster:${cluster.key}`
        nodes.push({
            id: clusterId,
            type: 'cluster',
            level: 1,
            clusterIndex: ci,
            key: cluster.key,
            entityType: parsed.type,
            entityDomain: parsed.domain,
            entityId: parsed.id,
            neighborType: `${parsed.type}~${parsed.domain}`,
            neighborCount: all.length,
            sharedCount: shared.length,
            visibleCount: visible.length,
            sharedIds: shared,
            neighborIds: all,
            includesSelf,
            movedInCount: movedCountFor(cluster.key, all),
            /* True when this cluster shares at least one neighbor with the
               root's own flattened neighbor list. */
            isShared: shared.length > 0,
        })
        pushEdge(rootId, clusterId, 'member')

        clusterSummaries.push({
            key: cluster.key,
            type: parsed.type,
            domain: parsed.domain,
            id: parsed.id,
            neighborCount: all.length,
            sharedCount: shared.length,
            visibleCount: visible.length,
            sharedIds: shared,
            neighborIds: all,
            includesSelf,
            movedInCount: movedCountFor(cluster.key, all),
        })

        if (!visible.length) return

        /* ── Shared members ──────────────────────────────────────────────
         * A member that is ALSO in flatten_neighbors_array is how the queried
         * entity reaches this cluster: the flatten array says it is the root's
         * neighbour, final_graph_map says it is the cluster's. It hangs off the
         * cluster like any other member — the cluster really does hold it — and
         * gains a second, dashed edge straight back to the root. That edge is
         * the connection this page exists to show. Members the root does not
         * share get no such edge.
         */
        const sharedVisible = visible.filter((v) => flattenSet.has(v))
        const otherVisible = visible.filter((v) => !flattenSet.has(v))

        /* Two edges: the cluster owns it, the root reaches it. */
        const pushSharedEdges = (nid) => {
            pushEdge(clusterId, nid, 'member')
            pushEdge(rootId, nid, 'direct')
        }

        /* 'all' draws shared members individually up to the cap; the other
           modes keep them grouped per type~domain so 99 ids stay one node. */
        const sharedSolo = mode === 'all' ? sharedVisible.slice(0, expandCap) : []
        sharedSolo.forEach((v) => {
            const nid = `${clusterId}|n|${v}`
            const p = parseEntityKey(v)
            nodes.push({
                id: nid,
                type: 'neighbor',
                level: 2,
                key: v,
                isSelf: false,
                neighborType: `${p.type}~${p.domain}`,
                entityType: p.type,
                entityDomain: p.domain,
                entityId: p.id,
                clusterKey: cluster.key,
                clusterIndex: ci,
                idCountInNode: 1,
                isShared: true,
                movedCount: movedCountFor(cluster.key, [v]),
            })
            pushSharedEdges(nid)
        })

        const bySharedTd = new Map()
        sharedVisible.slice(sharedSolo.length).forEach((v) => {
            const td = typeDomainOf(v)
            if (!bySharedTd.has(td)) bySharedTd.set(td, [])
            bySharedTd.get(td).push(v)
        })
        let li = 0
        bySharedTd.forEach((ids, td) => {
            const nid = `${clusterId}|link|${li++}`
            nodes.push({
                id: nid,
                type: 'connector',
                level: 2,
                key: td,
                neighborType: td,
                clusterKey: cluster.key,
                clusterIndex: ci,
                entityIds: ids,
                sharedIds: ids,
                idCountInNode: ids.length,
                isShared: true,
                containsSelf: ids.includes(rootKey),
                movedCount: movedCountFor(cluster.key, ids),
                movedIds: movedIdsFor(cluster.key, ids),
            })
            pushSharedEdges(nid)
        })

        if (!otherVisible.length) return

        /* Everything else belongs to the cluster alone and hangs off it. */
        if (mode === 'grouped') {
            const byTd = new Map()
            otherVisible.forEach((v) => {
                const td = typeDomainOf(v)
                if (!byTd.has(td)) byTd.set(td, [])
                byTd.get(td).push(v)
            })
            let gi = 0
            byTd.forEach((ids, td) => {
                const nid = `${clusterId}|g|${gi++}`
                nodes.push({
                    id: nid,
                    type: 'entity_group',
                    level: 2,
                    key: td,
                    neighborType: td,
                    clusterKey: cluster.key,
                    clusterIndex: ci,
                    entityIds: ids,
                    sharedIds: [],
                    idCountInNode: ids.length,
                    isShared: false,
                    containsSelf: ids.includes(rootKey),
                    movedCount: movedCountFor(cluster.key, ids),
                    movedIds: movedIdsFor(cluster.key, ids),
                })
                pushEdge(clusterId, nid, 'member')
            })
            return
        }

        /* 'connects' keeps the cluster-only members as a single summary — the
         * connectors are the story; 'all' draws them individually up to the cap.
         */
        const shown = mode === 'connects' ? [] : otherVisible.slice(0, expandCap)
        const hidden = otherVisible.slice(shown.length)

        shown.forEach((v) => {
            const nid = `${clusterId}|n|${v}`
            const p = parseEntityKey(v)
            nodes.push({
                id: nid,
                type: 'neighbor',
                level: 2,
                key: v,
                isSelf: v === rootKey,
                neighborType: `${p.type}~${p.domain}`,
                entityType: p.type,
                entityDomain: p.domain,
                entityId: p.id,
                clusterKey: cluster.key,
                clusterIndex: ci,
                idCountInNode: 1,
                isShared: false,
                movedCount: movedCountFor(cluster.key, [v]),
            })
            pushEdge(clusterId, nid, 'member')
        })

        if (hidden.length) {
            const bid = `${clusterId}|bundle`
            nodes.push({
                id: bid,
                type: 'entity_bundle',
                level: 2,
                key: `+${hidden.length}`,
                clusterKey: cluster.key,
                clusterIndex: ci,
                bundleTotal: otherVisible.length,
                bundleDisplayed: shown.length,
                bundleOverflow: hidden.length,
                bundleHiddenAllIds: hidden,
                bundleHiddenPreview: hidden.slice(0, BUNDLE_ID_PREVIEW),
                bundleHiddenNotListed: Math.max(0, hidden.length - BUNDLE_ID_PREVIEW),
                idCountInNode: hidden.length,
                movedCount: movedCountFor(cluster.key, hidden),
            })
            pushEdge(clusterId, bid, 'member')
        }
    })

    /* ── Neighbours of yours that no cluster holds ────────────────
     * flatten_neighbors_array lists them; no member list inside
     * final_graph_map does, so nothing ties them to a cluster. They are
     * still the root's own neighbours, and they hang off it on their own
     * branch. Reporting them only as a count — which is all the earlier
     * build did — left the "Your neighbours" total impossible to
     * reconcile with what the graph actually drew.
     */
    const directOnly = flatten.filter((v) => !coveredByCluster.has(v))
    const ownVisible = directOnly.filter((v) => matches(v))

    if (ownVisible.length) {
        const pushOwn = (n) => {
            nodes.push(n)
            pushEdge(rootId, n.id, 'own')
        }

        /* Same rule the clusters follow: 'all' draws IDs individually up to
           the cap, the other modes keep one node per type~domain. */
        const solo = mode === 'all' ? ownVisible.slice(0, expandCap) : []
        solo.forEach((v) => {
            const p = parseEntityKey(v)
            pushOwn({
                id: `own|n|${v}`,
                type: 'neighbor',
                level: 1,
                key: v,
                neighborType: `${p.type}~${p.domain}`,
                entityType: p.type,
                entityDomain: p.domain,
                entityId: p.id,
                idCountInNode: 1,
                isShared: false,
                isOwnOnly: true,
            })
        })

        const rest = ownVisible.slice(solo.length)
        if (mode === 'all') {
            if (rest.length) {
                pushOwn({
                    id: 'own|bundle',
                    type: 'entity_bundle',
                    level: 1,
                    key: `+${rest.length}`,
                    bundleTotal: ownVisible.length,
                    bundleDisplayed: solo.length,
                    bundleOverflow: rest.length,
                    bundleHiddenAllIds: rest,
                    bundleHiddenPreview: rest.slice(0, BUNDLE_ID_PREVIEW),
                    bundleHiddenNotListed: Math.max(0, rest.length - BUNDLE_ID_PREVIEW),
                    idCountInNode: rest.length,
                    isOwnOnly: true,
                })
            }
        } else {
            const byTd = new Map()
            rest.forEach((v) => {
                const td = typeDomainOf(v)
                if (!byTd.has(td)) byTd.set(td, [])
                byTd.get(td).push(v)
            })
            let oi = 0
            byTd.forEach((ids, td) => {
                pushOwn({
                    id: `own|g|${oi++}`,
                    type: 'entity_group',
                    level: 1,
                    key: td,
                    neighborType: td,
                    entityIds: ids,
                    sharedIds: [],
                    idCountInNode: ids.length,
                    isShared: false,
                    isOwnGroup: true,
                    isOwnOnly: true,
                })
            })
        }
    }

    const uniqueNeighbors = new Set()
    clusters.forEach((c) => c.values.forEach((v) => uniqueNeighbors.add(v)))

    return {
        rootKey,
        nodes,
        edges,
        clusters: clusterSummaries,
        flatten,
        /* Neighbours of yours that no cluster contains. Drawn off the root
           on their own branch; the count drives the stat strip. */
        directOnly,
        directOnlyCount: directOnly.length,
        stats: {
            clusters: clusters.length,
            neighbors: neighborTotal,
            uniqueNeighbors: uniqueNeighbors.size,
            shared: sharedTotal,
            uniqueShared: [...uniqueNeighbors].filter((v) => flattenSet.has(v)).length,
            flatten: flatten.length,
        },
    }
}

/* ── Merge curation: moving members between clusters ──────────────────────
 * A move is `{ key, from, to }`: the member `key` ("type~domain~id") leaves
 * cluster `from` for cluster `to`. Both are cluster keys as the query returns
 * them ("type~domain~id"); `from` is '' for one of your neighbours that no
 * cluster holds. Moves are unique per (from, key) — the same id can sit in two
 * clusters, and a move only relocates the copy it was dragged from.
 */

/** Cluster key -> Set of member keys as the query returned them; '' holds the neighbours in no cluster. */
export function clusterMembership(row) {
    const flatten = [...new Set(parseTildeList(row && row.flatten_neighbors_array))]
    const clusterList = [...new Set(parseTildeList(row && row.cluster_list))]
    const membership = new Map()
    parseFinalGraphMap(row && row.final_graph_map, new Set(clusterList)).forEach((e) => {
        membership.set(e.key, new Set(e.values))
    })
    clusterList.forEach((k) => {
        if (!membership.has(k)) membership.set(k, new Set())
    })
    const covered = new Set()
    membership.forEach((set) => set.forEach((v) => covered.add(v)))
    membership.set('', new Set(flatten.filter((v) => !covered.has(v))))
    return membership
}

/**
 * Applies `moves` to the parsed clusters in place and returns cluster key -> Set
 * of keys that arrived there. Removals run before additions, so swapping an id
 * between two clusters leaves it in both, as the two moves together say.
 */
export function applyClusterMovesToClusters(clusters, moves) {
    const movedIn = new Map()
    if (!Array.isArray(moves) || !moves.length) return movedIn
    const byKey = new Map(clusters.map((c) => [c.key, c]))
    moves.forEach((m) => {
        const src = m.from && byKey.get(m.from)
        if (src && byKey.has(m.to)) src.values = src.values.filter((v) => v !== m.key)
    })
    moves.forEach((m) => {
        const dst = byKey.get(m.to)
        if (!dst) return
        if (!dst.values.includes(m.key)) dst.values = dst.values.concat(m.key)
        if (!movedIn.has(m.to)) movedIn.set(m.to, new Set())
        movedIn.get(m.to).add(m.key)
    })
    return movedIn
}

/**
 * The moves after the user drops `keys` — as drawn under cluster `from` — onto
 * cluster `to`. What is drawn under `from` is its original members plus
 * whatever was already moved in, so both are carried on: a move that brought
 * the id into `from` is re-pointed, and an original member gets a new move.
 * Dropping an id back where it started cancels its move.
 */
export function addClusterMove(moves, { keys, from, to }, membership) {
    const src = String(from || '')
    if (!to || src === to) return moves
    const out = moves.map((m) => ({ ...m }))
    const original = (membership && membership.get(src)) || new Set()
    ;(keys || []).forEach((key) => {
        out.forEach((m) => {
            if (m.key === key && m.to === src) m.to = to
        })
        if (original.has(key) && !out.some((m) => m.key === key && m.from === src)) {
            out.push({ key, from: src, to })
        }
    })
    return out.filter((m) => m.from !== m.to)
}

/** "type~domain~id" cluster key -> the bare cluster id the truthset stores. */
export function clusterIdOfKey(clusterKey) {
    if (!clusterKey) return ''
    const p = parseEntityKey(clusterKey)
    return p ? p.id : ''
}

/**
 * source_cluster_id sent for every Cluster Formation move. Fixed for now rather
 * than the cluster the id was dragged from.
 */
export const CLUSTER_FORMATION_SOURCE_CLUSTER_ID = 'abc'

/**
 * Truthset body for the staged moves — one accepted, physical curation with an
 * entry per target cluster, ids grouped by type~domain. There is no
 * recommendation behind a Cluster Formation lookup, so recommendation_id is
 * '0', and source_cluster_id is CLUSTER_FORMATION_SOURCE_CLUSTER_ID.
 */
export function buildClusterFormationCurationPayload(moves) {
    const groups = new Map()
    ;(moves || []).forEach((m) => {
        const p = parseEntityKey(m.key)
        if (!p || !m.to) return
        const k = `${CLUSTER_FORMATION_SOURCE_CLUSTER_ID}\0${m.to}`
        if (!groups.has(k)) groups.set(k, new Map())
        const byTd = groups.get(k)
        const td = `${p.type}~${p.domain}`
        if (!byTd.has(td)) byTd.set(td, new Set())
        byTd.get(td).add(p.id)
    })
    const entity_lists = [...groups.entries()].map(([k, byTd]) => {
        const [from, to] = k.split('\0')
        const entity_list = {}
        byTd.forEach((ids, td) => {
            entity_list[td] = [...ids]
        })
        return {
            source_cluster_id: from,
            target_cluster_id: clusterIdOfKey(to),
            entity_list,
        }
    })
    return {
        curations: [
            {
                recommendation_id: '0',
                decision: 'accept',
                operation: 'physical',
                entity_lists,
            },
        ],
    }
}
